// src/pages/PrinterOpsSentry.jsx
// PrinterOps Sentry - AI-Powered Enterprise Print Fleet Management
// Edge AI Platform for HP Printer Operations
// MWC Barcelona Demo - Celerway Stratus

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK PRINTER FLEET DATA - Simulates HP enterprise print environment
// ════════════════════════════════════════════════════════════════════════════

const MOCK_SITE_GATEWAY = {
  id: 'gateway-hq-london',
  name: 'London HQ Print Hub',
  location: 'Canary Wharf Tower',
  status: 'Online',
  printerCount: 12,
  activeAlerts: 5,
  stratusModel: 'Stratus 5G Enterprise',
  connectivity: '5G + Ethernet',
  lastSync: new Date().toISOString(),
  monthlyPages: 142850,
  costPerPage: 0.023,
};

const MOCK_PRINTERS = [
  // Enterprise MFPs
  {
    id: 'MFP-LOBBY-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP LaserJet Enterprise MFP M635',
    location: 'Main Lobby',
    floor: 'Ground',
    status: 'warning',
    ipAddress: '192.168.10.101',
    serialNumber: 'CNBKM12345',
    metrics: {
      tonerBlack: 3,      // CRITICAL
      tonerCyan: 45,
      tonerMagenta: 52,
      tonerYellow: 38,
      paperLevel: 75,
      fuserLife: 78,
      drumLife: 65,
      pagesThisMonth: 12450,
      avgPagesPerDay: 415,
      jamRate: 0.2,       // % of jobs
      temperature: 42,    // Celsius
      uptime: 99.2,
    },
    lastMaintenance: '2024-11-15',
    nextMaintenance: '2025-02-15',
    connectivity: 'Online',
  },
  {
    id: 'MFP-EXEC-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP Color LaserJet Enterprise MFP M776',
    location: 'Executive Suite',
    floor: '12th',
    status: 'critical',
    ipAddress: '192.168.10.102',
    serialNumber: 'CNBKM12346',
    metrics: {
      tonerBlack: 28,
      tonerCyan: 15,
      tonerMagenta: 8,    // LOW
      tonerYellow: 22,
      paperLevel: 25,     // LOW
      fuserLife: 12,      // CRITICAL - end of life soon
      drumLife: 45,
      pagesThisMonth: 8920,
      avgPagesPerDay: 297,
      jamRate: 2.8,       // HIGH - needs service
      temperature: 58,    // HIGH
      uptime: 94.1,
    },
    lastMaintenance: '2024-09-20',
    nextMaintenance: '2025-01-20',  // Overdue!
    connectivity: 'Online',
  },
  {
    id: 'MFP-FLOOR3-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP LaserJet Enterprise MFP M528',
    location: 'Floor 3 Print Room',
    floor: '3rd',
    status: 'operational',
    ipAddress: '192.168.10.103',
    serialNumber: 'CNBKM12347',
    metrics: {
      tonerBlack: 67,
      tonerCyan: null,    // Mono only
      tonerMagenta: null,
      tonerYellow: null,
      paperLevel: 90,
      fuserLife: 82,
      drumLife: 71,
      pagesThisMonth: 18750,
      avgPagesPerDay: 625,
      jamRate: 0.4,
      temperature: 38,
      uptime: 99.8,
    },
    lastMaintenance: '2024-12-01',
    nextMaintenance: '2025-03-01',
    connectivity: 'Online',
  },
  {
    id: 'MFP-FLOOR5-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP Color LaserJet Enterprise MFP M681',
    location: 'Floor 5 Hub',
    floor: '5th',
    status: 'operational',
    ipAddress: '192.168.10.104',
    serialNumber: 'CNBKM12348',
    metrics: {
      tonerBlack: 54,
      tonerCyan: 61,
      tonerMagenta: 58,
      tonerYellow: 49,
      paperLevel: 85,
      fuserLife: 67,
      drumLife: 72,
      pagesThisMonth: 15230,
      avgPagesPerDay: 508,
      jamRate: 0.3,
      temperature: 40,
      uptime: 99.5,
    },
    lastMaintenance: '2024-11-28',
    nextMaintenance: '2025-02-28',
    connectivity: 'Online',
  },
  // Workgroup Printers
  {
    id: 'PR-FINANCE-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP LaserJet Pro M404dn',
    location: 'Finance Department',
    floor: '7th',
    status: 'warning',
    ipAddress: '192.168.10.201',
    serialNumber: 'CNBKM22345',
    metrics: {
      tonerBlack: 18,     // LOW
      tonerCyan: null,
      tonerMagenta: null,
      tonerYellow: null,
      paperLevel: 45,
      fuserLife: 55,
      drumLife: 48,
      pagesThisMonth: 28450,  // HIGH volume spike
      avgPagesPerDay: 948,    // Way above normal
      jamRate: 0.5,
      temperature: 44,
      uptime: 98.9,
    },
    lastMaintenance: '2024-10-15',
    nextMaintenance: '2025-01-15',
    connectivity: 'Online',
    baselineAvgPages: 320,  // Normal is 320/day, currently 948
  },
  {
    id: 'PR-HR-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP LaserJet Pro M404n',
    location: 'HR Department',
    floor: '6th',
    status: 'operational',
    ipAddress: '192.168.10.202',
    serialNumber: 'CNBKM22346',
    metrics: {
      tonerBlack: 72,
      tonerCyan: null,
      tonerMagenta: null,
      tonerYellow: null,
      paperLevel: 80,
      fuserLife: 89,
      drumLife: 85,
      pagesThisMonth: 4250,
      avgPagesPerDay: 142,
      jamRate: 0.1,
      temperature: 36,
      uptime: 99.9,
    },
    lastMaintenance: '2024-12-10',
    nextMaintenance: '2025-03-10',
    connectivity: 'Online',
  },
  {
    id: 'PR-LEGAL-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP Color LaserJet Pro M454dw',
    location: 'Legal Department',
    floor: '8th',
    status: 'operational',
    ipAddress: '192.168.10.203',
    serialNumber: 'CNBKM22347',
    metrics: {
      tonerBlack: 45,
      tonerCyan: 52,
      tonerMagenta: 48,
      tonerYellow: 55,
      paperLevel: 70,
      fuserLife: 76,
      drumLife: 68,
      pagesThisMonth: 6780,
      avgPagesPerDay: 226,
      jamRate: 0.2,
      temperature: 39,
      uptime: 99.4,
    },
    lastMaintenance: '2024-11-20',
    nextMaintenance: '2025-02-20',
    connectivity: 'Online',
  },
  // Production Printers
  {
    id: 'PROD-MAILROOM-01',
    type: 'Production Printer',
    category: 'production',
    model: 'HP PageWide Enterprise 586',
    location: 'Mailroom',
    floor: 'Ground',
    status: 'operational',
    ipAddress: '192.168.10.301',
    serialNumber: 'CNBKM32345',
    metrics: {
      tonerBlack: 62,
      tonerCyan: 58,
      tonerMagenta: 61,
      tonerYellow: 54,
      paperLevel: 95,
      fuserLife: 71,
      drumLife: 78,
      pagesThisMonth: 35200,
      avgPagesPerDay: 1173,
      jamRate: 0.3,
      temperature: 41,
      uptime: 99.1,
    },
    lastMaintenance: '2024-12-05',
    nextMaintenance: '2025-03-05',
    connectivity: 'Online',
  },
  {
    id: 'PROD-MARKETING-01',
    type: 'Production Printer',
    category: 'production',
    model: 'HP Color LaserJet Enterprise M856',
    location: 'Marketing Print Center',
    floor: '4th',
    status: 'operational',
    ipAddress: '192.168.10.302',
    serialNumber: 'CNBKM32346',
    metrics: {
      tonerBlack: 48,
      tonerCyan: 42,
      tonerMagenta: 45,
      tonerYellow: 39,
      paperLevel: 65,
      fuserLife: 58,
      drumLife: 62,
      pagesThisMonth: 22150,
      avgPagesPerDay: 738,
      jamRate: 0.4,
      temperature: 43,
      uptime: 98.7,
    },
    lastMaintenance: '2024-11-10',
    nextMaintenance: '2025-02-10',
    connectivity: 'Online',
  },
  // Print Server
  {
    id: 'PRSRV-01',
    type: 'Print Server',
    category: 'server',
    model: 'HP JetDirect Print Server',
    location: 'Server Room',
    floor: '2nd',
    status: 'operational',
    ipAddress: '192.168.10.10',
    serialNumber: 'CNBKM42345',
    metrics: {
      cpuUsage: 34,
      memoryUsage: 52,
      queuedJobs: 12,
      activeConnections: 48,
      jobsToday: 2847,
      avgProcessTime: 1.2,  // seconds
      uptime: 99.99,
      diskSpace: 72,
    },
    lastMaintenance: '2024-12-20',
    connectivity: 'Online',
  },
  // Offline printer
  {
    id: 'PR-WAREHOUSE-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP LaserJet Pro M404dn',
    location: 'Warehouse Office',
    floor: 'Warehouse',
    status: 'offline',
    ipAddress: '192.168.10.250',
    serialNumber: 'CNBKM22399',
    metrics: {
      tonerBlack: 45,
      tonerCyan: null,
      tonerMagenta: null,
      tonerYellow: null,
      paperLevel: 60,
      fuserLife: 65,
      drumLife: 70,
      pagesThisMonth: 1250,
      avgPagesPerDay: 42,
      jamRate: 0,
      temperature: null,
      uptime: 78.5,
    },
    lastMaintenance: '2024-08-15',
    nextMaintenance: '2024-11-15',  // Overdue
    connectivity: 'Offline',
    lastSeen: '2025-01-31T10:45:00Z',  // 4+ hours ago
  },
  // Wide Format
  {
    id: 'WF-DESIGN-01',
    type: 'Wide Format',
    category: 'wideformat',
    model: 'HP DesignJet T650',
    location: 'Design Studio',
    floor: '9th',
    status: 'operational',
    ipAddress: '192.168.10.401',
    serialNumber: 'CNBKM52345',
    metrics: {
      inkBlack: 55,
      inkCyan: 62,
      inkMagenta: 58,
      inkYellow: 51,
      rollMedia: 85,       // % remaining
      printHead: 72,       // % life
      pagesThisMonth: 245, // Large format prints
      avgPagesPerDay: 8,
      temperature: 35,
      uptime: 99.2,
    },
    lastMaintenance: '2024-12-12',
    nextMaintenance: '2025-03-12',
    connectivity: 'Online',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES - Printer Fleet specific
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  CRITICAL: { icon: '🔴', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50', color: 'text-red-400' },
  WARNING: { icon: '🟡', text: 'WARNING', bg: 'bg-amber-500', glow: 'from-amber-500/50 to-yellow-500/50', color: 'text-amber-400' },
  OPTIMIZING: { icon: '⚡', text: 'OPTIMIZING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', color: 'text-purple-400', pulse: true },
  HEALTHY: { icon: '✓', text: 'HEALTHY', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30', color: 'text-emerald-400' },
  OFFLINE: { icon: '○', text: 'OFFLINE', bg: 'bg-slate-500', glow: 'from-slate-500/30 to-gray-500/30', color: 'text-slate-400' },
};

const CATEGORY_STYLES = {
  mfp: { icon: '🖨️', color: 'text-blue-300', bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60', label: 'Enterprise MFP' },
  workgroup: { icon: '📠', color: 'text-emerald-300', bg: 'from-emerald-400/30 to-teal-500/30', border: 'border-emerald-400/60', label: 'Workgroup' },
  production: { icon: '🏭', color: 'text-purple-300', bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60', label: 'Production' },
  server: { icon: '🖥️', color: 'text-cyan-300', bg: 'from-cyan-400/30 to-blue-500/30', border: 'border-cyan-400/60', label: 'Print Server' },
  wideformat: { icon: '📐', color: 'text-amber-300', bg: 'from-amber-400/30 to-orange-500/30', border: 'border-amber-400/60', label: 'Wide Format' },
};

// ════════════════════════════════════════════════════════════════════════════
// AI ANOMALY DETECTION - Print Fleet Management
// ════════════════════════════════════════════════════════════════════════════

const detectPrinterAnomalies = (printers) => {
  const anomalies = [];
  const now = Date.now();
  
  printers.forEach(printer => {
    const m = printer.metrics;
    
    // Skip servers for supply checks
    if (printer.category === 'server') {
      // Server-specific checks
      if (m.cpuUsage > 80) {
        anomalies.push({
          id: `cpu-${printer.id}`,
          type: 'SERVER_HIGH_CPU',
          printerId: printer.id,
          severity: 'warning',
          metric: `${m.cpuUsage}%`,
          description: 'Print server CPU usage high',
          recommendation: 'Consider load balancing print queues across secondary server.',
          confidence: 88,
        });
      }
      return;
    }
    
    // Offline device
    if (printer.connectivity === 'Offline' || printer.status === 'offline') {
      const lastSeen = printer.lastSeen ? new Date(printer.lastSeen).getTime() : null;
      const hoursOffline = lastSeen ? Math.floor((now - lastSeen) / 3600000) : 'Unknown';
      
      anomalies.push({
        id: `offline-${printer.id}`,
        type: 'DEVICE_OFFLINE',
        printerId: printer.id,
        severity: 'critical',
        metric: `${hoursOffline}h offline`,
        description: `Device offline for ${hoursOffline} hours`,
        location: printer.location,
        recommendation: 'Check network connectivity and power. Dispatch technician if remote restart fails.',
        confidence: 95,
        action: {
          type: 'DISPATCH_TECH',
          description: 'Create service ticket',
        },
      });
      return; // Skip other checks for offline devices
    }
    
    // Toner critical (< 10%)
    const tonerLevels = [
      { name: 'Black', level: m.tonerBlack },
      { name: 'Cyan', level: m.tonerCyan },
      { name: 'Magenta', level: m.tonerMagenta },
      { name: 'Yellow', level: m.tonerYellow },
    ].filter(t => t.level !== null);
    
    tonerLevels.forEach(toner => {
      if (toner.level <= 5) {
        anomalies.push({
          id: `toner-${printer.id}-${toner.name.toLowerCase()}`,
          type: 'TONER_CRITICAL',
          printerId: printer.id,
          severity: 'critical',
          metric: `${toner.level}%`,
          tonerColor: toner.name,
          description: `${toner.name} toner critically low`,
          location: printer.location,
          recommendation: `Order replacement ${toner.name.toLowerCase()} toner immediately. Estimated pages remaining: ~${Math.floor(toner.level * 50)}`,
          confidence: 99,
          action: {
            type: 'AUTO_ORDER',
            description: 'Order via HP Smart Device Services',
            sku: `HP-${printer.model.includes('Color') ? 'CF' : 'CF'}XXX-${toner.name.charAt(0)}`,
          },
        });
      } else if (toner.level <= 20) {
        anomalies.push({
          id: `toner-low-${printer.id}-${toner.name.toLowerCase()}`,
          type: 'TONER_LOW',
          printerId: printer.id,
          severity: 'warning',
          metric: `${toner.level}%`,
          tonerColor: toner.name,
          description: `${toner.name} toner running low`,
          location: printer.location,
          recommendation: `Schedule toner replacement. Approximately ${Math.floor(toner.level * 50)} pages remaining.`,
          confidence: 95,
          action: {
            type: 'SCHEDULE_ORDER',
            description: 'Add to next supply order',
          },
        });
      }
    });
    
    // Fuser end of life (< 15%)
    if (m.fuserLife && m.fuserLife < 15) {
      anomalies.push({
        id: `fuser-${printer.id}`,
        type: 'FUSER_EOL',
        printerId: printer.id,
        severity: 'critical',
        metric: `${m.fuserLife}%`,
        description: 'Fuser nearing end of life',
        location: printer.location,
        recommendation: `Schedule fuser replacement. Approximately ${m.fuserLife * 200} pages remaining before failure risk.`,
        confidence: 94,
        action: {
          type: 'SCHEDULE_MAINTENANCE',
          description: 'Schedule preventive maintenance',
        },
      });
    }
    
    // High jam rate (> 2%)
    if (m.jamRate && m.jamRate > 2) {
      anomalies.push({
        id: `jam-${printer.id}`,
        type: 'HIGH_JAM_RATE',
        printerId: printer.id,
        severity: 'warning',
        metric: `${m.jamRate}%`,
        description: 'Paper jam rate elevated',
        location: printer.location,
        recommendation: 'Check paper path, rollers, and paper quality. Schedule cleaning if issue persists.',
        confidence: 87,
        action: {
          type: 'SCHEDULE_SERVICE',
          description: 'Schedule service visit',
        },
      });
    }
    
    // High temperature
    if (m.temperature && m.temperature > 55) {
      anomalies.push({
        id: `temp-${printer.id}`,
        type: 'HIGH_TEMPERATURE',
        printerId: printer.id,
        severity: m.temperature > 60 ? 'critical' : 'warning',
        metric: `${m.temperature}°C`,
        description: 'Device running hot',
        location: printer.location,
        recommendation: 'Check ventilation and ambient temperature. Consider redistributing print load.',
        confidence: 91,
        action: {
          type: 'LOAD_BALANCE',
          description: 'Redistribute print jobs',
        },
      });
    }
    
    // Volume spike (> 200% of baseline)
    if (printer.baselineAvgPages && m.avgPagesPerDay > printer.baselineAvgPages * 2) {
      const percentIncrease = Math.floor((m.avgPagesPerDay / printer.baselineAvgPages - 1) * 100);
      anomalies.push({
        id: `volume-${printer.id}`,
        type: 'VOLUME_SPIKE',
        printerId: printer.id,
        severity: 'warning',
        metric: `+${percentIncrease}%`,
        description: `Print volume ${percentIncrease}% above baseline`,
        location: printer.location,
        recommendation: 'Investigate unusual print activity. May indicate unauthorized use or process change.',
        confidence: 82,
      });
    }
    
    // Paper low (< 30%)
    if (m.paperLevel && m.paperLevel < 30) {
      anomalies.push({
        id: `paper-${printer.id}`,
        type: 'PAPER_LOW',
        printerId: printer.id,
        severity: 'warning',
        metric: `${m.paperLevel}%`,
        description: 'Paper supply low',
        location: printer.location,
        recommendation: 'Refill paper trays soon to avoid interruption.',
        confidence: 98,
      });
    }
    
    // Maintenance overdue
    if (printer.nextMaintenance) {
      const maintenanceDate = new Date(printer.nextMaintenance).getTime();
      if (maintenanceDate < now) {
        const daysOverdue = Math.floor((now - maintenanceDate) / 86400000);
        anomalies.push({
          id: `maintenance-${printer.id}`,
          type: 'MAINTENANCE_OVERDUE',
          printerId: printer.id,
          severity: daysOverdue > 30 ? 'critical' : 'warning',
          metric: `${daysOverdue}d overdue`,
          description: `Scheduled maintenance ${daysOverdue} days overdue`,
          location: printer.location,
          recommendation: 'Schedule maintenance immediately to prevent failures.',
          confidence: 96,
          action: {
            type: 'SCHEDULE_MAINTENANCE',
            description: 'Schedule maintenance visit',
          },
        });
      }
    }
  });
  
  return anomalies.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getPrinterAIStatus = (printer, anomalies) => {
  const printerAnomalies = anomalies.filter(a => a.printerId === printer.id);
  
  if (printer.connectivity === 'Offline' || printer.status === 'offline') return AI_STATUS.OFFLINE;
  
  const hasCritical = printerAnomalies.some(a => a.severity === 'critical');
  const hasWarning = printerAnomalies.some(a => a.severity === 'warning');
  
  if (hasCritical) return AI_STATUS.CRITICAL;
  if (hasWarning) return AI_STATUS.WARNING;
  return AI_STATUS.HEALTHY;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// Print Operations Center Node
const PrintOpsNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-cyan-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[260px] shadow-2xl shadow-cyan-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <span className="text-3xl">🖨️</span>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.printerCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Devices</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.monthlyPages ? `${Math.floor(data.monthlyPages/1000)}K` : '0'}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Pages/Mo</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-300">${data.costPerPage || '0.00'}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Per Page</div>
        </div>
      </div>
      
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-[10px] text-cyan-300 font-medium">HP Smart Device Services</span>
      </div>
    </div>
  </div>
);

// Site Gateway Node
const SiteGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[260px] shadow-xl transition-all duration-300 group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'} shadow-lg`} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location}</div>
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-slate-400">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>{data.status}</div>
          </div>
          <div className="text-center">
            <span className="text-slate-400">Printers</span>
            <div className="font-semibold text-blue-300">{data.printerCount}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Alerts</span>
            <div className={`font-semibold ${data.activeAlerts > 0 ? 'text-amber-300' : 'text-slate-300'}`}>{data.activeAlerts}</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel} • {data.connectivity}
          </span>
        </div>
      </div>
    </div>
  );
};

// Printer Node
const PrinterNode = ({ data }) => {
  const categoryStyle = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.workgroup;
  const aiStatus = data.aiStatus || AI_STATUS.HEALTHY;
  const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
  const m = data.metrics || {};
  
  // Get lowest toner level for color devices
  const tonerLevels = [m.tonerBlack, m.tonerCyan, m.tonerMagenta, m.tonerYellow].filter(t => t !== null && t !== undefined);
  const lowestToner = tonerLevels.length > 0 ? Math.min(...tonerLevels) : null;
  
  const renderMetrics = () => {
    if (data.category === 'server') {
      return (
        <div className="grid grid-cols-3 gap-1 text-center">
          <div className={`rounded px-1 py-0.5 ${m.cpuUsage > 80 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
            <div className={`text-[10px] font-bold ${m.cpuUsage > 80 ? 'text-amber-300' : 'text-slate-300'}`}>{m.cpuUsage}%</div>
            <div className="text-[8px] text-slate-500">CPU</div>
          </div>
          <div className="rounded px-1 py-0.5 bg-slate-700/50">
            <div className="text-[10px] font-bold text-slate-300">{m.queuedJobs}</div>
            <div className="text-[8px] text-slate-500">QUEUE</div>
          </div>
          <div className="rounded px-1 py-0.5 bg-slate-700/50">
            <div className="text-[10px] font-bold text-slate-300">{m.jobsToday}</div>
            <div className="text-[8px] text-slate-500">JOBS</div>
          </div>
        </div>
      );
    }
    
    if (data.category === 'wideformat') {
      return (
        <div className="grid grid-cols-3 gap-1 text-center">
          <div className={`rounded px-1 py-0.5 ${m.inkBlack < 20 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
            <div className={`text-[10px] font-bold ${m.inkBlack < 20 ? 'text-amber-300' : 'text-slate-300'}`}>{m.inkBlack}%</div>
            <div className="text-[8px] text-slate-500">INK</div>
          </div>
          <div className="rounded px-1 py-0.5 bg-slate-700/50">
            <div className="text-[10px] font-bold text-slate-300">{m.rollMedia}%</div>
            <div className="text-[8px] text-slate-500">MEDIA</div>
          </div>
          <div className="rounded px-1 py-0.5 bg-slate-700/50">
            <div className="text-[10px] font-bold text-slate-300">{m.pagesThisMonth}</div>
            <div className="text-[8px] text-slate-500">PRINTS</div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-3 gap-1 text-center">
        <div className={`rounded px-1 py-0.5 ${lowestToner !== null && lowestToner < 20 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
          <div className={`text-[10px] font-bold ${lowestToner !== null && lowestToner < 20 ? 'text-red-300' : 'text-slate-300'}`}>
            {lowestToner !== null ? `${lowestToner}%` : 'N/A'}
          </div>
          <div className="text-[8px] text-slate-500">TONER</div>
        </div>
        <div className={`rounded px-1 py-0.5 ${m.paperLevel < 30 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
          <div className={`text-[10px] font-bold ${m.paperLevel < 30 ? 'text-amber-300' : 'text-slate-300'}`}>{m.paperLevel}%</div>
          <div className="text-[8px] text-slate-500">PAPER</div>
        </div>
        <div className="rounded px-1 py-0.5 bg-slate-700/50">
          <div className="text-[10px] font-bold text-slate-300">{m.pagesThisMonth ? `${Math.floor(m.pagesThisMonth/1000)}K` : '0'}</div>
          <div className="text-[8px] text-slate-500">PAGES</div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {hasAlert && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? 'border-red-400' : 'border-amber-400') : categoryStyle.border} rounded-lg p-3 min-w-[175px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* AI Status Badge */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        {/* Printer header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br ${categoryStyle.bg}`}>
            <span className="text-xl">{categoryStyle.icon}</span>
          </div>
          <div>
            <div className="text-xs font-bold text-white">{data.id}</div>
            <div className="text-[10px] text-slate-400">{categoryStyle.label}</div>
          </div>
        </div>
        
        {/* Location */}
        <div className="text-[10px] text-slate-500 mb-1">{data.location}</div>
        
        {/* Model */}
        <div className="text-[9px] text-slate-400 mb-2 truncate">{data.model}</div>
        
        {/* Connectivity status */}
        <div className="mb-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            data.connectivity === 'Online' ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-400/30' :
            'bg-red-500/30 text-red-300 border border-red-400/30'
          }`}>
            {data.connectivity}
          </span>
        </div>
        
        {/* Metrics */}
        {renderMetrics()}
        
        {/* Uptime */}
        {m.uptime && (
          <div className="mt-2 text-center">
            <span className="text-[9px] text-slate-500">Uptime: {m.uptime}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  printOps: PrintOpsNode,
  siteGateway: SiteGatewayNode,
  printer: PrinterNode,
};

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL
// ════════════════════════════════════════════════════════════════════════════

const AIActivityPanel = ({ isOpen, onClose, anomalies, printers }) => {
  const [activeTab, setActiveTab] = useState('alerts');
  const [executingAction, setExecutingAction] = useState(null);
  
  if (!isOpen) return null;
  
  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExecutingAction(null);
  };
  
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const totalPages = printers.reduce((sum, p) => sum + (p.metrics?.pagesThisMonth || 0), 0);
  
  return (
    <div className="absolute top-0 right-0 h-full w-96 bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">🖨️</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AI Sentry PrinterOps</h3>
              <div className="text-[10px] text-slate-400">{anomalies.length} alerts • {printers.length} devices</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Fleet Monitoring
          </div>
          <div className="text-[10px] text-slate-500">HP Smart Integration</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('alerts')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'alerts'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🚨 Alerts
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('supplies')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'supplies'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          📦 Supplies
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'analytics'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          📊 Analytics
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'alerts' && (
          <>
            {anomalies.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div>All printers healthy</div>
              </div>
            ) : (
              anomalies.map(anomaly => {
                const printer = printers.find(p => p.id === anomaly.printerId);
                const categoryStyle = CATEGORY_STYLES[printer?.category] || CATEGORY_STYLES.workgroup;
                return (
                  <div
                    key={anomaly.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      anomaly.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-3`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{categoryStyle.icon}</span>
                        <div>
                          <div className="text-sm font-semibold text-white">{anomaly.printerId}</div>
                          <div className="text-[10px] text-slate-400">{anomaly.location || printer?.location}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${anomaly.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                          {anomaly.confidence}%
                        </div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">{anomaly.description}</div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {anomaly.metric}
                      </span>
                      <span className="text-[10px] text-slate-500">{anomaly.type.replace(/_/g, ' ')}</span>
                    </div>
                    
                    <div className="bg-slate-700/30 rounded p-2 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">AI Recommendation</div>
                      <div className="text-xs text-cyan-300">{anomaly.recommendation}</div>
                    </div>
                    
                    {anomaly.action && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleExecuteAction(anomaly)}
                          disabled={executingAction === anomaly.id}
                          className="flex-1 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1"
                        >
                          {executingAction === anomaly.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>✓ {anomaly.action.type.replace(/_/g, ' ')}</>
                          )}
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'supplies' && (
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg border border-cyan-500/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">📦</span>
                <div>
                  <div className="text-sm font-semibold text-white">Auto-Order Recommendation</div>
                  <div className="text-[10px] text-slate-400">HP Smart Device Services</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-bold text-emerald-400">99%</div>
                  <div className="text-[10px] text-slate-500">Confidence</div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-2">Items to Order</div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-300">HP 655A Black Toner</span>
                    <span className="text-red-300 font-mono">MFP-LOBBY-01</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-300">HP 655A Magenta Toner</span>
                    <span className="text-amber-300 font-mono">MFP-EXEC-01</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-300">HP Fuser Kit M776</span>
                    <span className="text-red-300 font-mono">MFP-EXEC-01</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-400">Estimated Total</span>
                  <span className="text-white font-semibold">£847.50</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Delivery</span>
                  <span className="text-emerald-300">Next Business Day</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                  🛒 Place Order
                </button>
                <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">
                  Edit
                </button>
              </div>
            </div>
            
            <div className="text-center text-[10px] text-slate-500 pt-2">
              Connected to <span className="text-cyan-400">HP Smart Device Services</span>
            </div>
          </div>
        )}
        
        {activeTab === 'analytics' && (
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Fleet Overview - This Month</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-cyan-300">{Math.floor(totalPages/1000)}K</div>
                  <div className="text-xs text-slate-400">Total Pages</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-emerald-300">98.2%</div>
                  <div className="text-xs text-slate-400">Avg Uptime</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-amber-300">£3,285</div>
                  <div className="text-xs text-slate-400">Print Cost</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-purple-300">0.4%</div>
                  <div className="text-xs text-slate-400">Avg Jam Rate</div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Top Printers by Volume</div>
              <div className="space-y-2">
                {printers
                  .filter(p => p.metrics?.pagesThisMonth)
                  .sort((a, b) => (b.metrics?.pagesThisMonth || 0) - (a.metrics?.pagesThisMonth || 0))
                  .slice(0, 5)
                  .map((printer, idx) => (
                    <div key={printer.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{idx + 1}.</span>
                        <span className="text-sm text-slate-300">{printer.id}</span>
                      </div>
                      <span className="text-sm font-medium text-cyan-300">
                        {Math.floor((printer.metrics?.pagesThisMonth || 0)/1000)}K
                      </span>
                    </div>
                  ))}
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Environmental Impact</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <div className="text-xl font-bold text-emerald-300">12.4K</div>
                  <div className="text-[10px] text-slate-400">kWh Energy</div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <div className="text-xl font-bold text-emerald-300">2.1 T</div>
                  <div className="text-[10px] text-slate-400">CO₂ Saved</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Last sync: Just now</span>
          <button className="text-cyan-400 hover:text-cyan-300">Refresh Now</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT CARD
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ anomalies, printers, onViewDetails }) => {
  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  if (criticalAnomalies.length === 0) return null;
  
  const topAnomaly = criticalAnomalies[0];
  const printer = printers.find(p => p.id === topAnomaly.printerId);
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/90 to-slate-900/90 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl animate-pulse">🖨️</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white mb-1">
              Supply Alert
            </div>
            <div className="text-xs text-red-200 mb-2">
              {topAnomaly.description}
            </div>
            <div className="text-[10px] text-slate-400 mb-3">
              {topAnomaly.printerId} • {printer?.location} • {criticalAnomalies.length > 1 ? `+${criticalAnomalies.length - 1} more alerts` : topAnomaly.type.replace(/_/g, ' ')}
            </div>
            <button 
              onClick={onViewDetails}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ printers, anomalies, gateway, onOpenPanel }) => {
  const online = printers.filter(p => p.connectivity === 'Online').length;
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const totalPages = printers.reduce((sum, p) => sum + (p.metrics?.pagesThisMonth || 0), 0);
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-emerald-300 font-bold text-lg">{online}</span>
        <span className="text-slate-300 text-xs">/ {printers.length} Online</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-cyan-300 font-bold text-lg">{Math.floor(totalPages/1000)}K</span>
        <span className="text-slate-300 text-xs">Pages/Mo</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">Alerts</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg px-4 py-2 border border-cyan-400/50 transition-colors"
      >
        <span className="text-cyan-300 text-sm">🤖</span>
        <span className="text-cyan-300 text-xs font-medium">AI Sentry</span>
        {anomalies.length > 0 && (
          <span className="px-1.5 py-0.5 bg-cyan-500 text-white text-[10px] rounded-full">{anomalies.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-sm">{style.icon}</span>
          <span className="text-xs text-slate-300">{style.label}</span>
        </div>
      ))}
    </div>
    <div className="pt-2 border-t border-slate-600/50 space-y-2">
      <div className="text-[10px] text-slate-400 uppercase">Status</div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-red-500" />
        <span className="text-xs text-slate-300">Critical</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-amber-500" />
        <span className="text-xs text-slate-300">Warning</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-emerald-500" />
        <span className="text-xs text-slate-300">Healthy</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const PrinterOpsSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [printers] = useState(MOCK_PRINTERS);
  const [gateway] = useState(MOCK_SITE_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Build topology
  const buildTopology = useCallback(() => {
    const detectedAnomalies = detectPrinterAnomalies(printers);
    setAnomalies(detectedAnomalies);
    
    const newNodes = [];
    const newEdges = [];
    
    // Layout constants
    const CLOUD_Y = 0;
    const GATEWAY_Y = 180;
    const PRINTER_Y_START = 400;
    const CENTER_X = 600;
    const PRINTER_GAP = 195;
    const CATEGORY_GAP = 180;
    
    // Group printers by category
    const categories = ['mfp', 'workgroup', 'production', 'server', 'wideformat'];
    const printersByCategory = {};
    categories.forEach(cat => {
      printersByCategory[cat] = printers.filter(p => p.category === cat);
    });
    
    // Calculate totals
    const totalPages = printers.reduce((sum, p) => sum + (p.metrics?.pagesThisMonth || 0), 0);
    
    // 1. Print Operations Center (Cloud)
    newNodes.push({
      id: 'print-ops',
      type: 'printOps',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'Print Operations',
        sublabel: 'Fleet Management Center',
        printerCount: printers.length,
        monthlyPages: totalPages,
        costPerPage: gateway.costPerPage,
      },
    });
    
    // 2. Site Gateway
    newNodes.push({
      id: `gateway-${gateway.id}`,
      type: 'siteGateway',
      position: { x: CENTER_X, y: GATEWAY_Y },
      data: { ...gateway, activeAlerts: detectedAnomalies.length },
    });
    
    // Edge: Cloud → Gateway
    newEdges.push({
      id: 'cloud-to-gateway',
      source: 'print-ops',
      target: `gateway-${gateway.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#22d3ee', strokeWidth: 2 },
    });
    
    // 3. Printers by category in rows
    let currentY = PRINTER_Y_START;
    
    categories.forEach(category => {
      const categoryPrinters = printersByCategory[category];
      if (categoryPrinters.length === 0) return;
      
      const startX = CENTER_X - ((categoryPrinters.length - 1) * PRINTER_GAP) / 2;
      
      categoryPrinters.forEach((printer, idx) => {
        const aiStatus = getPrinterAIStatus(printer, detectedAnomalies);
        const printerId = `printer-${printer.id}`;
        
        newNodes.push({
          id: printerId,
          type: 'printer',
          position: { x: startX + idx * PRINTER_GAP, y: currentY },
          data: { ...printer, aiStatus },
        });
        
        // Edge: Gateway → Printer
        const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
        const edgeColor = hasAlert 
          ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b')
          : printer.connectivity === 'Offline' ? '#64748b' : '#22d3ee';
        
        newEdges.push({
          id: `gateway-to-${printerId}`,
          source: `gateway-${gateway.id}`,
          target: printerId,
          type: 'smoothstep',
          animated: printer.connectivity !== 'Offline',
          style: { stroke: edgeColor, strokeWidth: 2 },
        });
      });
      
      currentY += CATEGORY_GAP;
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [printers, gateway]);

  // Initialize
  useEffect(() => {
    setLoading(true);
    
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center border border-cyan-400/30">
                <span className="text-xl">🖨️</span>
              </span>
              PrinterOps Sentry
            </h1>
            <p className="text-slate-300 text-sm mt-1">AI-Powered Fleet Management • Predictive Maintenance • Supply Automation</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded border border-cyan-500/30">
                HP Smart Device Services
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                MWC Barcelona 2025
              </span>
            </div>
          </div>
          <StatsBar 
            printers={printers} 
            anomalies={anomalies}
            gateway={gateway}
            onOpenPanel={() => setIsAIPanelOpen(true)} 
          />
        </div>
      </div>
      
      {/* Proactive Alert */}
      <ProactiveAlert 
        anomalies={anomalies} 
        printers={printers} 
        onViewDetails={() => setIsAIPanelOpen(true)} 
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">AI Sentry Initializing...</div>
            <div className="text-slate-400 text-sm mt-1">Connecting to Print Fleet</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(6, 182, 212, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'printOps') return '#22d3ee';
              if (node.type === 'siteGateway') return '#34d399';
              if (node.data?.aiStatus === AI_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.WARNING) return '#f59e0b';
              if (node.data?.aiStatus === AI_STATUS.OFFLINE) return '#64748b';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      {/* AI Panel */}
      <AIActivityPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        anomalies={anomalies}
        printers={printers}
      />
      
      {/* Animations */}
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default PrinterOpsSentry;
